import React, { Fragment } from "react";
import logo from "../../assets/icon/asten-global-logo-client.png";
import SearchBar from "../searchBar/SearchBar";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CategoriesData } from "../../fakeData/CategoriesData";
import { searchAction } from "../../store/reducers/searchSlice";
import Declaration from "../declaration/Declaration";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Navigation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleClick = (value) => {
    navigate("/search?Category=" + value);
    dispatch(searchAction({ value: value, path: "Category" }));
  };

  return (
    <div className="sticky bg-brand top-0 z-20">
      {/* Top bar */}
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-10">
        <div className="h-16 top-bar lg:h-auto flex items-center justify-between mx-auto">
          <Link
            to="/"
            className="!no-underline mr-3 hidden xl:mr-12 md:hidden lg:block"
          >
           <img
              alt="Ashten Global Logo"
              className="flex items-center my-2 py-1 rounded-md bg-white"
              style={{ width: '90px', height: '79px' }}
              src={logo}
           />
          </Link>
          <SearchBar />
          <div className="hidden md:hidden md:items-center lg:flex xl:flex absolute inset-y-0 right-0 pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <Declaration />
          </div>
        </div>
      </div>

      {/* Navbar */}
      <div className="hidden lg:block xl:block bg-white border-b">
        <div className="items-center justify-center flex mx-auto max-w-screen-2xl px-3 sm:px-10 h-12 bg-gradient-to-r from-green-200 via-indigo-100 to-green-100">
          <div className="inline-flex">
            <Popover className="relative">
              <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center md:justify-start md:space-x-10">
                  <Popover.Group
                    as="nav"
                    className="md:flex space-x-10 items-center"
                  >
                    {/* Products Dropdown */}
                    <Popover className="relative">
                      {({ open }) => (
                        <>
                          <Popover.Button
                            className={classNames(
                              open ? "text-emerald-600" : "text-black",
                              "text-sm font-medium flex items-center !no-underline hover:!text-emerald-600"
                            )}
                          >
                            <span>Products</span>
                            <ChevronDownIcon
                              className={classNames(
                                open ? "text-emerald-600" : "text-gray-500",
                                "ml-1 h-4 w-4"
                              )}
                              aria-hidden="true"
                            />
                          </Popover.Button>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <Popover.Panel className="absolute left-0 z-20 mt-3 w-screen max-w-4xl px-2 sm:px-0">
                              <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden bg-white">
                                <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 gap-4 p-6">
                                  {CategoriesData.map((category, index) => (
                                    <li key={index} className="group">
                                      <div className="flex flex-col bg-white border border-gray-100 p-4 rounded-lg shadow-sm hover:shadow-md transition">
                                        {/* Category icon */}
                                        <img
                                          alt={category.name}
                                          src={category.icon}
                                          className="h-10 w-10 object-contain mb-2"
                                        />
                                        {/* Category name */}
                                        <h3 className="text-sm font-medium leading-tight line-clamp-1">
                                          <Link
                                            className="!no-underline !text-gray-800 group-hover:!text-emerald-500"
                                            to={"/search?Category=" + category.path}
                                          >
                                            {category.name}
                                          </Link>
                                        </h3>
                                        {/* Subcategories */}
                                        <ul className="pt-1 mt-1">
                                          {category.subCategories.map(
                                            (subCategory, subIndex) => (
                                              <li className="pt-1" key={subIndex}>
                                                <Link
                                                  onClick={() =>
                                                    handleClick(subCategory.path)
                                                  }
                                                  className="flex items-center text-xs !no-underline !text-gray-600 hover:!text-emerald-600"
                                                  to={
                                                    "/search?Category=" +
                                                    subCategory.path
                                                  }
                                                >
                                                  <svg
                                                    stroke="currentColor"
                                                    fill="currentColor"
                                                    strokeWidth="0"
                                                    viewBox="0 0 512 512"
                                                    height="0.8em"
                                                    width="0.8em"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="mr-1"
                                                  >
                                                    <path
                                                      fill="none"
                                                      strokeLinecap="square"
                                                      strokeMiterlimit="10"
                                                      strokeWidth="48"
                                                      d="M184 112l144 144-144 144"
                                                    ></path>
                                                  </svg>
                                                  {subCategory.name}
                                                </Link>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </Popover.Panel>
                          </Transition>
                        </>
                      )}
                    </Popover>

                    {/* Other Links */}
                    <Link
                      className="text-sm !text-black !no-underline hover:!text-emerald-600"
                      to="/about-us"
                    >
                      About Us
                    </Link>
                    <Link
                      className="text-sm !text-black !no-underline hover:!text-emerald-600"
                      to="/contact-us"
                    >
                      Contact Us
                    </Link>
                    <Link
                      className="text-sm !text-black !no-underline hover:!text-emerald-600"
                      to="/FAQ"
                    >
                      FAQ
                    </Link>
                    <Link
                      className="text-sm !text-black !no-underline hover:!text-emerald-600"
                      to="/home"
                    >
                      Home
                    </Link>
                  </Popover.Group>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Navigation;